import { LosseLink, LossePlaatjie, useLoaderData } from '@ubo/losse-sjedel'
import dayjs from 'dayjs'
import { Fragment } from 'react'
import Animated from '~/components/elements/Animated'
import Content from '~/components/elements/Content'
import GreenGreenGrass from '~/components/elements/GreenGreenGrass'
import type { Page_Flexcontent_Flex_Hubspot, RootQueryToExpertConnection } from '~/graphql/types'
import { type ProfilePageLoaderData } from '~/services/hubspot.server'
import { phases } from './HubspotBanner'

type Props = Page_Flexcontent_Flex_Hubspot & {
  subdata?: {
    Advisors: RootQueryToExpertConnection
  }
}

export default function HubspotBannerAlt({ fields }: { fields: Props }) {
  if (typeof document !== 'undefined') {
    const url = new URL(document.location.href)

    // set url in sessionStorage
    if (localStorage.getItem('profield_profile_url') !== url.href) {
      localStorage.setItem('profield_profile_url', url.href)
    }
  }

  const { hubspotData: profile } = useLoaderData<ProfilePageLoaderData>()
  const currentPhase = phases.find((phase) => phase.id.some((id) => String(id) === profile.deals[0].properties.dealstage))
  const matches = profile.deals.filter((deal) => currentPhase.id.some((id) => id === Number(deal.properties.dealstage)))

  const advisor = fields.subdata?.Advisors?.nodes.find((advisor) => {
    if (currentPhase.step === 2) {
      return (
        advisor.title?.toLowerCase() ===
          `${profile.properties.carerix_first_meeting_owner_firstname} ${profile.properties.carerix_first_meeting_owner_lastname}`.toLowerCase() ||
        advisor.recap.email?.toLowerCase() === profile.properties.carerix_first_meeting_owner_email?.toLowerCase()
      )
    }

    if (currentPhase.step === 4) {
      return (
        advisor.title?.toLowerCase() ===
          `${profile.properties.carerix_job_owner_firstname} ${profile.properties.carerix_job_owner_lastname}`.toLowerCase() ||
        advisor.recap.email?.toLowerCase() === profile.properties.carerix_job_owner_email?.toLowerCase()
      )
    }

    if (currentPhase.step === 5) {
      return (
        advisor.title?.toLowerCase() ===
          `${profile.properties.carerix_job_owner_firstname} ${profile.properties.carerix_job_owner_lastname}`.toLowerCase() ||
        advisor.recap.email?.toLowerCase() === profile.properties.carerix_job_owner_email?.toLowerCase()
      )
    }

    return (
      advisor.title?.toLowerCase() === `${profile.owner.firstName} ${profile.owner.lastName}`.toLowerCase() ||
      advisor.recap.email?.toLowerCase() === profile.owner.email?.toLowerCase()
    )
  })

  return (
    <section data-component="HubspotBannerAlt" className="bg-site-base pb-20 lg:pb-[250px] pt-[100px] lg:pt-[150px] -mt-[100px] relative">
      <Animated delay={0}>
        <div className="absolute top-0 right-0 w-[642px]">
          <GreenGreenGrass className="w-full max-sm:opacity-30" />
        </div>
      </Animated>
      <div className="container">
        <div className="max-w-[1069px] mx-auto">
          <Animated delay={0}>
            <div className="text-xs w-fit border-2 border-white text-white rounded-full px-4 py-2">Pro Portal</div>
          </Animated>
          <Animated delay={100}>
            <h1 className="title--medium !leading-[1.2] text-white mt-8">
              {fields.title
                .replace('{name}', profile.properties.firstname)

                ?.split('|')
                ?.map((t, i) => (
                  <Fragment key={i}>
                    <span key={i}>{t}</span>
                    <br className="last:hidden" />
                  </Fragment>
                ))}
            </h1>
          </Animated>
          <Animated delay={200}>
            <div>
              <Content className="max-w-[580px] mt-3 children-p:text-xl children-p:font-bold children-p:text-site-accent">
                {fields.subtext?.replace('{signing_name}', matches[0].properties.carerix_signing_location_name)}
              </Content>
            </div>
          </Animated>
          <Animated delay={300}>
            <div className="mt-3">
              <Content className="children-p:!text-white max-w-[400px]">
                {fields.description.replace('{signing_name}', profile.properties.carerix_job_company_name)}
              </Content>
            </div>
          </Animated>
          <div className="grid grid-cols-1 xs:grid-cols-2 lg:grid-cols-7 gap-y-5 xs:gap-y-10 lg:gap-y-14 mt-8 xs:mt-14 lg:mt-28 gap-x-6 lg:gap-x-10 text-white">
            <Animated delay={100}>
              <div className="lg:col-span-2">
                <h3 className="font-bold mb-1 xs:mb-2">Opdrachtgever</h3>
                <span>{profile.properties.carerix_job_company_name}</span>
              </div>
            </Animated>
            <Animated delay={100}>
              <div className="lg:col-span-2">
                <h3 className="font-bold mb-1 xs:mb-2">Startdatum</h3>
                <span>{dayjs(profile.properties.carerix_job_startdate).format('D MMMM YYYY')}</span>
              </div>
            </Animated>
            <Animated delay={100}>
              <div className="lg:col-span-2">
                <h3 className="font-bold mb-1 xs:mb-2">Aantal uren</h3>
                <span>{profile.properties.carerix_job_hours_per_week} uur</span>
              </div>
            </Animated>
            <Animated delay={100}>
              <div className="lg:col-span-2">
                <h3 className="font-bold mb-1 xs:mb-2">CAO Opdrachtgever</h3>
                <span>{profile.properties.carerix_job_company_name}</span>
              </div>
            </Animated>
            {advisor && (
              <Animated delay={150}>
                <div className="xs:col-span-2 lg:row-span-2 lg:col-span-3 lg:col-start-5 lg:row-start-1 flex max-lg:justify-center items-center">
                  <div className="bg-white max-w-[440px] max-xs:mt-3 w-full flex gap-x-4 items-center p-3">
                    <div>
                      <LossePlaatjie className="h-[111px] w-[111px] object-cover" src={advisor.featuredImage.node} />
                    </div>
                    <div className="flex flex-col justify-center">
                      <div className="text-site-base">Persoonlijke Field Manager</div>
                      <div className="text-site-base font-bold text-xl">{advisor.title}</div>
                      <LosseLink
                        target={advisor.recap.whatsapp.target || '_blank'}
                        to={advisor.recap.whatsapp.url}
                        className="flex items-center gap-2 mt-3 hover:underline text-site-accent"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none">
                          <path
                            d="M3.16699 15.8333L4.11172 12.4898C3.50477 11.4615 3.1881 10.2907 3.1881 9.08834C3.1881 5.38625 6.20171 2.375 9.90671 2.375C13.6117 2.375 16.6253 5.38625 16.6253 9.08834C16.6253 12.7904 13.6117 15.8017 9.90671 15.8017C8.75088 15.8017 7.62144 15.5064 6.61866 14.9474L3.16699 15.8333ZM6.7981 13.7239L7.00394 13.8504C7.87477 14.3831 8.87755 14.6626 9.90671 14.6626C12.9837 14.6626 15.4853 12.1629 15.4853 9.08834C15.4853 6.01381 12.9837 3.51411 9.90671 3.51411C6.82977 3.51411 4.3281 6.01381 4.3281 9.08834C4.3281 10.1589 4.63421 11.1978 5.20949 12.0943L5.34671 12.3105L4.8031 14.2354L6.7981 13.7239Z"
                            fill="#78BF00"
                          />
                          <path
                            d="M7.91366 5.57176L7.45608 5.54224C7.31275 5.53634 7.17492 5.58357 7.06466 5.68395C6.84414 5.8906 6.49132 6.2862 6.38106 6.80579C6.22118 7.57928 6.46926 8.52399 7.11428 9.4687C7.75929 10.4134 8.96663 11.925 11.1001 12.5685C11.7893 12.7752 12.3295 12.6335 12.743 12.3501C13.0738 12.1257 13.2998 11.7596 13.3825 11.3522L13.4542 10.9861C13.4762 10.8681 13.4211 10.7559 13.3218 10.7027L11.7893 9.94696C11.69 9.89973 11.5687 9.92925 11.5026 10.0237L10.8962 10.8622C10.8521 10.9271 10.7749 10.9507 10.7032 10.9212C10.2897 10.7677 8.90047 10.1418 8.13968 8.57122C8.10661 8.50037 8.11212 8.41771 8.16174 8.36457L8.7406 7.65013C8.80124 7.57928 8.81227 7.473 8.77919 7.38443L8.11763 5.71937C8.08456 5.63671 8.00186 5.57767 7.91366 5.57176Z"
                            fill="#78BF00"
                          />
                        </svg>
                        {advisor.recap.whatsapp.title}
                      </LosseLink>
                    </div>
                  </div>
                </div>
              </Animated>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
